import { ONE_DAY_MS, ONE_HOUR_MS } from '@eflight/shared';
import { useCallback, useEffect, useState } from 'react';

import { type ChildEvent, type Reservation } from '../../../Admin/Scheduling/model/SchedulingTypes';
import { ResourceRole } from '../../../Admin/Scheduling/model/SchedulingTypes';
import { useSyncState } from '../../../reducers/syncReducer';
import { useSignedTlbUserState } from '../../../reducers/tlbUserReducer';
import { toMap } from '../../../utils';
import { useOnSupplementalDataSync, useOnSync } from '../../../utils/useOnSync';
import { type FinishedFlightLeg } from '../../FlightLegs/FlightLeg';
import { isLegFinished } from '../../FlightLegs/FlightLeg';
import { getDb } from '../../LocalDB/LocalDB';
import { userIsCrew } from '../../Users/User';
import { type FlightSupplementalReservation } from '../FlightSupplementalDataTypes';
import { FlightSupplementalDataType } from '../FlightSupplementalDataTypes';
import { findContactInReservation } from './FindContactInReservation';

export function useUpcomingReservations(hoursForesight = 12): Map<string, UpcomingReservationEvent[]> | undefined {
    const [reservationsToOffer, setReservationsToOffer] = useState<Map<string, UpcomingReservationEvent[]> | undefined>(
        undefined
    );
    const [reload, setReload] = useState<number>(0);
    const contactId = useCurrentUserContactId();
    useEffect(() => {
        let cancelled = false;
        getUpcomingReservationEventsByAircraft(contactId, hoursForesight).then((newReservationsToOffer) => {
            if (!cancelled) {
                setReservationsToOffer(newReservationsToOffer);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [reload, hoursForesight, contactId]);
    useOnSupplementalDataSync(() => {
        setReload(Math.random());
    });
    return reservationsToOffer;
}

export type UpcomingReservationEvent = {
    reservation: Reservation;
    event: ChildEvent;
    aircraftTitle: string;
    aircraftId: string;
};

export async function getUpcomingReservationEvents(
    currentContactId: string | null,
    hoursForesight = 12,
    includeSimpleReservations = false
): Promise<UpcomingReservationEvent[]> {
    const reservations = await getDb()
        .flightSupplementalData.where({ type: FlightSupplementalDataType.reservation })
        .toArray();
    const allEvents: UpcomingReservationEvent[] = reservations.flatMap((data): UpcomingReservationEvent[] => {
        const { reservation } = data as FlightSupplementalReservation;
        const aircraftResource = reservation.resources.find((r) => r.role === ResourceRole.aircraft)?.resource;
        if (!aircraftResource) {
            return [];
        }
        const aircraftId = aircraftResource.resourceId;
        const aircraftTitle = aircraftResource.title || '?';
        const events = reservation.events?.length
            ? reservation.events
            : includeSimpleReservations
              ? [createDummyEvent(reservation)]
              : [];
        const validEvents = events.filter((e) => !e.deleted);
        return validEvents.map((event) => ({ reservation, event, aircraftId, aircraftTitle }));
    });
    const currentUserEvents = currentContactId
        ? allEvents.filter((event) => findContactInReservation(event.reservation, currentContactId))
        : allEvents;
    const earliestDate = Date.now() - ONE_DAY_MS;
    const latestDate = Date.now() + hoursForesight * ONE_HOUR_MS;
    const reasonablyUpcomingEvents = currentUserEvents.filter(
        (event) => event.event.dateTo.getTime() > earliestDate && event.event.dateFrom.getTime() < latestDate
    );
    reasonablyUpcomingEvents.sort((a, b) => a.event.dateFrom.getTime() - b.event.dateFrom.getTime());
    return reasonablyUpcomingEvents;
}

/**
 * Travel reservations the contact travels on. They are simple reservations — no aircraft, no legs —
 * so they never appear among `getUpcomingReservationEvents`, and are read from the same cache
 * separately.
 */
export async function getUpcomingTravelReservations(
    currentContactId: string | null,
    hoursForesight = 14 * 24
): Promise<Reservation[]> {
    if (!currentContactId) {
        return [];
    }
    const rows = await getDb().flightSupplementalData.where({ type: FlightSupplementalDataType.reservation }).toArray();
    const earliestDate = Date.now() - ONE_DAY_MS;
    const latestDate = Date.now() + hoursForesight * ONE_HOUR_MS;

    return rows
        .map((row) => (row as FlightSupplementalReservation).reservation)
        .filter(
            (reservation) =>
                reservation.travel !== undefined &&
                !reservation.deleted &&
                findContactInReservation(reservation, currentContactId) &&
                reservation.dateTo.getTime() > earliestDate &&
                reservation.dateFrom.getTime() < latestDate
        )
        .sort((a, b) => a.dateFrom.getTime() - b.dateFrom.getTime());
}

export function useUpcomingTravelReservations(hoursForesight = 14 * 24): Reservation[] {
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [refresh, setRefresh] = useState<number>(0);
    const contactId = useCurrentUserContactId();
    useEffect(() => {
        let cancelled = false;
        getUpcomingTravelReservations(contactId, hoursForesight).then((loaded) => {
            if (!cancelled) {
                setReservations(loaded);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [refresh, hoursForesight, contactId]);
    useOnSupplementalDataSync(() => {
        setRefresh(Math.random());
    });

    return reservations;
}

function createDummyEvent(reservation: Reservation): ChildEvent {
    return {
        dateFrom: new Date(reservation.dateFrom),
        dateTo: new Date(reservation.dateTo),
        id: '',
        reservationId: reservation.id,
        title: reservation.title || '',
        isUnconfirmed: reservation.isUnconfirmed,
        resources: reservation.resources,
        type: reservation.type,
        createdAt: reservation.createdAt,
    };
}

export async function getUpcomingReservationEventsByAircraft(
    currentContactId: string | null,
    hoursForesight = 12
): Promise<Map<string, UpcomingReservationEvent[]>> {
    const [byAircraftMap, allEvents] = await getNextReservationEvent(currentContactId, hoursForesight);
    const out = new Map<string, UpcomingReservationEvent[]>();
    for (const [aircraftId, firstEvent] of byAircraftMap.entries()) {
        const aircraftEvents = allEvents.filter((e) => e.aircraftId === aircraftId);
        if (!firstEvent) {
            continue;
        }
        const firstEventIndex = aircraftEvents.indexOf(firstEvent);
        if (firstEventIndex === -1) {
            continue;
        }
        out.set(aircraftId, aircraftEvents.slice(firstEventIndex));
    }
    return out;
}

export async function getReservationById(reservationId: string): Promise<Reservation | null> {
    const db = getDb();
    const item = (await db.flightSupplementalData.get(reservationId)) as FlightSupplementalReservation | undefined;
    return item ? item.reservation : null;
}

export type NextReservationEventTuple = [
    Map<string, UpcomingReservationEvent | null | undefined>, // most likely next event for each aircraft
    UpcomingReservationEvent[], // all upcoming events
];
export function useNextReservationEvent(
    hoursForesight = 12,
    includeSimpleReservations = false
): NextReservationEventTuple {
    const [tuple, setTuple] = useState<NextReservationEventTuple>([new Map(), []]);
    const [refresh, setRefresh] = useState<number>(Math.random());
    const contactId = useCurrentUserContactId();
    // A locally finished leg moves the next event: re-read on every local enqueue (works offline) and sync.
    const { queueLengthUp } = useSyncState();
    useOnSync(useCallback(() => setRefresh(Math.random()), []));
    useEffect(() => {
        let cancelled = false;
        getNextReservationEvent(contactId, hoursForesight, includeSimpleReservations).then((events) => {
            if (!cancelled) {
                setTuple(events);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [refresh, queueLengthUp, hoursForesight, contactId, includeSimpleReservations]);
    useOnSupplementalDataSync(() => {
        setRefresh(Math.random());
    });
    return tuple;
}

async function getNextReservationEvent(
    currentContactId: string | null,
    hoursForesight = 12,
    includeSimpleReservations = false
): Promise<NextReservationEventTuple> {
    const upcomingEvents = await getUpcomingReservationEvents(
        currentContactId,
        hoursForesight,
        includeSimpleReservations
    );
    const byAircraftMap = await getMostLikelyNextReservationEventByAircraft(upcomingEvents);

    return [byAircraftMap, upcomingEvents];
}

export async function getMostLikelyNextReservationEventByAircraft(
    upcomingEvents: UpcomingReservationEvent[]
): Promise<Map<string, UpcomingReservationEvent>> {
    const out = new Map<string, UpcomingReservationEvent>();
    const allAircraftIds = [...new Set(upcomingEvents.map((e) => e.aircraftId))];
    for (const aircraftId of allAircraftIds) {
        const eventsOnAircraft = upcomingEvents.filter((e) => e.aircraftId === aircraftId);
        const legsForAircraft = await getValidFinishedLegsForAircraft(aircraftId);
        if (legsForAircraft.length) {
            const aircraftLocation = legsForAircraft[0].airportOnIcao;
            const eventsAlreadyFlown = new Set(legsForAircraft.map((l) => l.reservationEventId));
            const legsNotAlreadyFlown = eventsOnAircraft.filter((e) => !eventsAlreadyFlown.has(e.event.id));
            const legsAircraftCanImmediatelyStart = legsNotAlreadyFlown.filter(
                // starts on aircraft location and not already flown
                (e) => e.event.location === aircraftLocation
            );
            if (legsAircraftCanImmediatelyStart.length) {
                out.set(aircraftId, legsAircraftCanImmediatelyStart[0]);
            } else if (legsNotAlreadyFlown.length) {
                out.set(aircraftId, legsNotAlreadyFlown[0]);
            }
        } else {
            out.set(aircraftId, eventsOnAircraft[0]);
        }
    }
    return out;
}

export async function getValidFinishedLegsForAircraft(aircraftId: string): Promise<FinishedFlightLeg[]> {
    const db = getDb();
    const [legs, flights] = await Promise.all([
        db.legs.where({ aircraftId }).toArray(),
        db.flights.where({ aircraftId }).toArray(),
    ]);
    const flightsMap = toMap(flights);
    const validLegs = legs.filter((leg) => {
        if (!isLegFinished(leg)) {
            return false;
        }
        if (leg.deleted) {
            return false;
        }
        const flight = flightsMap.get(leg.flightId);
        return !(!flight || flight.deleted);
    }) as FinishedFlightLeg[];
    validLegs.sort((a, b) => {
        return b.timeOut.getTime() - a.timeOut.getTime();
    });
    return validLegs;
}

export function useCurrentUserContactId() {
    const user = useSignedTlbUserState();
    const userData = user.data;
    if (!userIsCrew(userData)) {
        return null;
    }
    return userData.contactId;
}
